import axiosInstance, {
  configMultiPartFormData,
  fetcher,
  revalidateOnlyStaleOptions,
} from 'src/utils/axios';
import { ENDPOINTS } from './endpoints';
import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import { getURL } from 'src/utils/url';
import { isArray } from 'src/utils/type_check';
import { isEmpty } from 'lodash';

const MANAGEMENT_TYPES = ['rack', 'tote', 'cart'];

function mutateByPrefix(prefixes) {
  return mutate(
    (key) => typeof key === 'string' && prefixes.some((prefix) => key.startsWith(prefix)),
  );
}

function mutateLocationList() {
  return mutateByPrefix([ENDPOINTS.location.get_list]);
}

function mutateManagementList(type) {
  const types = type ? [type] : MANAGEMENT_TYPES;
  return mutateByPrefix(types.map((each) => ENDPOINTS.location.get_management_list(each)));
}

export function useGetLocations(params) {
  if (isEmpty(params)) {
    params = {
      pageSize: 100,
      pageNumber: 1,
      locationId: '',
    };
  }
  const URL = getURL(ENDPOINTS.location.get_list, params);
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);
  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result?.list || [],
      searchCount: {
        location: data?.result?.count.totalCount || 0,
      },
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  return memoizedValue;
}

export async function createLocation(data) {
  var ret = await axiosInstance.post(ENDPOINTS.location.post, data);
  await mutateLocationList();
  return ret.data;
}

export async function editLocation(data, locationId) {
  const endpoint = ENDPOINTS.location.put(locationId);
  const response = await axiosInstance.put(endpoint, data);
  await mutate(ENDPOINTS.location.get(locationId));
  await mutateLocationList();
  return response.data;
}

export async function deleteLocation(locationId) {
  const endpoint = ENDPOINTS.location.put(locationId);
  const response = await axiosInstance.delete(endpoint);
  await mutate(ENDPOINTS.location.get(locationId));
  await mutateLocationList();
  return response.data;
}

export function useGetLocationManagement(type, params, skip = false) {
  const URL = skip ? null : getURL(ENDPOINTS.location.get_management_list(type), params);
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);
  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || {},
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  return memoizedValue;
}

export function useGetRacks(params) {
  let URL = null;
  const locationId = params.locationId;

  if (!isEmpty(locationId)) {
    URL = getURL(ENDPOINTS.location.get_racks(locationId), params);
  }
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);
  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  if (URL === null) {
    return {
      searchResults: [],
      searchLoading: false,
      searchError: null,
      searchValidating: false,
    };
  }
  return memoizedValue;
}

export function useGetRackSlots(locationId, rackNumber, params) {
  let URL = null;
  if (!isEmpty(locationId) && !isEmpty(rackNumber)) {
    URL = getURL(ENDPOINTS.location.get_rack_info(locationId, rackNumber), params);
  }
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);
  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || {},
      searchSuccess: data?.isSuccess,
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, data?.isSuccess, error, isLoading, isValidating],
  );
  if (URL === null) {
    return {
      searchResults: [],
      searchLoading: false,
      searchError: null,
      searchValidating: false,
    };
  }
  return memoizedValue;
}

export async function getRackBarcode(locationId, rackBarcode) {
  const URL = ENDPOINTS.location.get_rack_barcode(
    locationId,
    `${rackBarcode ?? ''}`.trim().toUpperCase(),
  );
  const res = await axiosInstance.get(URL);
  return res.data;
}

export async function createRackOrTote(data, type) {
  var isSuccess = false;
  var ret = await axiosInstance.post(ENDPOINTS.location.post_rack_or_tote(type), data);
  if (ret.data.isSuccess) {
    isSuccess = true;
  }
  if (isSuccess) {
    await mutateManagementList(type);
  }
  return ret.data;
}

export async function updateTote(data, id, locationId) {
  var isSuccess = false;
  var ret = await axiosInstance.put(ENDPOINTS.location.update_tote(id, locationId), data);
  if (ret.data.isSuccess) {
    isSuccess = true;
  }
  if (isSuccess) {
    await mutateToteItems(locationId, id);
    await mutateManagementList('tote');
  }
  return ret.data;
}

export async function editRack(data, locationId, rackNumber) {
  var isSuccess = false;
  const URL = ENDPOINTS.location.put_rack_info(locationId, rackNumber);
  var ret = await axiosInstance.put(URL, data);
  if (ret.data.isSuccess) {
    isSuccess = true;
  }
  if (isSuccess) {
    mutate(ENDPOINTS.location.get_rack_info(locationId, rackNumber), (d) => d, true);
    await mutateManagementList('rack');
  }
  return ret.data;
}

export async function deleteRackOrTote(ids, type) {
  if (!isArray(ids)) ids = [ids];
  var isSuccess = true;
  var errorMessages = [];
  var blockers = [];
  for (var i = 0; i < ids.length; i++) {
    var data;
    try {
      var ret = await axiosInstance.delete(
        ENDPOINTS.location.delete_rack_or_tote(ids[i].id, ids[i].locationId, type),
      );
      data = ret.data;
    } catch (error) {
      data = error?.response?.data ?? { isSuccess: false };
    }
    if (!data?.isSuccess) {
      isSuccess = false;
      errorMessages = errorMessages.concat(data?.errorMessages ?? []);
      blockers = blockers.concat(data?.result?.blockers ?? []);
    }
  }
  await mutate(
    (key) =>
      typeof key === 'string' && key.startsWith(ENDPOINTS.location.get_management_list(type)),
  );
  return { isSuccess, errorMessages, blockers };
}

export function useGetLocation(id) {
  const shouldFetch = !!id ? ENDPOINTS.location.get(id) : false;
  const { data, isLoading, error, isValidating } = useSWR(
    shouldFetch,
    fetcher,
    revalidateOnlyStaleOptions,
  );
  const memoizedValue = useMemo(
    () => ({
      searchLocation: data?.result?.location || {},
      searchCouriers: data?.result?.courierList || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}
export function useGetToteItem(params) {
  const locationId = params.locationId;
  const toteNumber = params.toteNumber;

  const url = getURL(ENDPOINTS.location.tote_item(locationId, toteNumber), params);
  const { data, isLoading, error, isValidating } = useSWR(url, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      searchList: data?.result[0]?.toteItems || [],
      searchCouriers: data?.result?.courierList || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export async function addToteItem(data) {
  const endpoint = ENDPOINTS.location.add_tote_item;
  const response = await axiosInstance.post(endpoint, data);
  await mutateToteItems(data.locationId, data.toteNumber);

  return response.data;
}

async function mutateToteItems(locationId, toteNumber) {
  await mutate(
    (key) =>
      typeof key === 'string' &&
      key.startsWith(ENDPOINTS.location.tote_item(locationId, toteNumber)),
  );
  await mutate(
    (key) =>
      typeof key === 'string' && key.startsWith(ENDPOINTS.location.get_management_list('tote')),
  );
}

async function mutateCartItems(locationId, cartNumber) {
  await mutate(
    (key) =>
      typeof key === 'string' &&
      key.startsWith(ENDPOINTS.location.get_cart(locationId, cartNumber)),
  );
  await mutate(
    (key) =>
      typeof key === 'string' && key.startsWith(ENDPOINTS.location.get_management_list('cart')),
  );
}

export async function updateToteItemBarcodesBulk(locationId, toteNumber, items) {
  const response = await axiosInstance.put(
    ENDPOINTS.location.put_tote_item_bulk(locationId, toteNumber),
    { items: items },
  );
  await mutateToteItems(locationId, toteNumber);
  return response.data;
}

export async function updateCartItemBarcodesBulk(locationId, cartNumber, items) {
  const response = await axiosInstance.put(
    ENDPOINTS.location.put_cart_item_bulk(locationId, cartNumber),
    { items: items },
  );
  await mutateCartItems(locationId, cartNumber);
  return response.data;
}

export async function importToteOrCartExcel(type, locationId, excelFile) {
  const formData = new FormData();
  formData.append('excelFile', excelFile);
  formData.append('locationId', locationId);
  const endpoint =
    type === 'cart'
      ? ENDPOINTS.location.post_cart_excel_import
      : ENDPOINTS.location.post_tote_excel_import;
  const response = await axiosInstance.post(endpoint, formData, configMultiPartFormData);
  await mutate(
    (key) =>
      typeof key === 'string' && key.startsWith(ENDPOINTS.location.get_management_list(type)),
  );
  return response.data;
}

export async function addRackSlot(data) {
  const endpoint = ENDPOINTS.location.add_rack_slot;
  const response = await axiosInstance.post(endpoint, data);

  return response.data;
}

export async function createCart(data) {
  var isSuccess = false;
  var ret = await axiosInstance.post(ENDPOINTS.location.post_cart, data);
  if (ret.data.isSuccess) {
    isSuccess = true;
  }
  if (isSuccess) {
    await mutateManagementList('cart');
  }
  return ret.data;
}

export function useGetCart(params) {
  let URL = null;

  if (!isEmpty(params.locationId)) {
    URL = getURL(ENDPOINTS.location.get_cart(params.locationId, params.cartNumber));
  }
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);
  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result[0]?.cartItems || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  if (URL === null) {
    return {
      searchResults: [],
      searchLoading: false,
      searchError: null,
      searchValidating: false,
    };
  }
  return memoizedValue;
}

export async function addCart(data) {
  const endpoint = ENDPOINTS.location.add_cart;
  const response = await axiosInstance.post(endpoint, data);
  await mutateCartItems(data.locationId, data.cartNumber);

  return response.data;
}

export async function updateCart(data, locationId, cartNumber) {
  var isSuccess = false;
  var ret = await axiosInstance.put(ENDPOINTS.location.put_cart(locationId, cartNumber), data);
  if (ret.data.isSuccess) {
    isSuccess = true;
  }
  if (isSuccess) {
    await mutateCartItems(locationId, cartNumber);
    await mutateManagementList('cart');
  }
  return ret.data;
}
